/* Interactive global investment map — pure SVG. */
import { useState } from "react";
import { TEST_IDS } from "../../constants/testIds";

const NODES = [
  { id: "providence", label: "Providence, RI", region: "North America", x: 285, y: 172, sector: "Real Assets & Technology" },
  { id: "houston", label: "Houston, TX", region: "North America", x: 240, y: 210, sector: "Energy" },
  { id: "lagos", label: "Lagos", region: "Africa", x: 490, y: 300, sector: "Infrastructure" },
  { id: "accra", label: "Accra", region: "Africa", x: 480, y: 296, sector: "Energy" },
  { id: "riyadh", label: "Riyadh", region: "Middle East", x: 585, y: 250, sector: "Sovereign AI Compute" },
  { id: "beijing", label: "Beijing", region: "Asia", x: 770, y: 180, sector: "Energy & Commodities" },
  { id: "guangzhou", label: "Guangzhou", region: "Asia", x: 775, y: 240, sector: "LPG Infrastructure" },
  { id: "jakarta", label: "Jakarta", region: "Asia", x: 800, y: 320, sector: "Petrochemicals" },
  { id: "shandong", label: "Shandong", region: "Asia", x: 780, y: 200, sector: "Refining" },
];

export default function GlobalMap() {
  const [active, setActive] = useState(null);
  return (
    <div className="relative w-full" data-testid={TEST_IDS.home.globalMap}>
      <svg viewBox="0 0 960 480" className="w-full h-auto" role="img" aria-label="Global investment presence">
        {/* Background */}
        <rect width="960" height="480" fill="#0B1F3A" />
        {/* Latitude lines */}
        {[80, 140, 200, 260, 320, 380, 440].map(y => (
          <line key={y} x1="0" y1={y} x2="960" y2={y} stroke="rgba(212,175,55,0.06)" strokeWidth="0.5" />
        ))}
        {[120, 240, 360, 480, 600, 720, 840].map(x => (
          <line key={x} x1={x} y1="0" x2={x} y2="480" stroke="rgba(212,175,55,0.06)" strokeWidth="0.5" />
        ))}

        {/* Continent silhouettes (abstract) */}
        <g fill="rgba(248,247,242,0.06)" stroke="rgba(248,247,242,0.15)" strokeWidth="0.5">
          {/* North America */}
          <path d="M120,130 L200,110 L260,120 L300,150 L310,200 L280,240 L230,260 L200,250 L170,220 L140,190 Z" />
          {/* South America */}
          <path d="M270,280 L310,290 L320,340 L305,400 L280,420 L260,380 L255,320 Z" />
          {/* Europe */}
          <path d="M430,120 L490,110 L520,140 L510,180 L470,200 L440,180 L430,150 Z" />
          {/* Africa */}
          <path d="M460,210 L520,220 L540,270 L530,340 L500,390 L470,370 L455,320 L450,260 Z" />
          {/* Middle East */}
          <path d="M550,200 L610,210 L620,250 L595,280 L560,260 Z" />
          {/* Asia */}
          <path d="M620,110 L780,100 L860,140 L880,200 L840,240 L790,260 L740,240 L680,220 L620,190 Z" />
          {/* SE Asia */}
          <path d="M770,270 L830,280 L850,320 L810,340 L775,325 Z" />
          {/* Australia */}
          <path d="M820,370 L890,370 L900,410 L850,425 L820,400 Z" />
        </g>

        {/* Connection arcs from Providence to nodes */}
        {NODES.filter(n => n.id !== "providence").map(n => {
          const from = { x: 285, y: 172 };
          const mx = (from.x + n.x) / 2;
          const my = Math.min(from.y, n.y) - 60;
          return (
            <path
              key={`arc-${n.id}`}
              d={`M ${from.x} ${from.y} Q ${mx} ${my} ${n.x} ${n.y}`}
              stroke="rgba(212,175,55,0.35)"
              strokeWidth="0.7"
              fill="none"
              strokeDasharray="2 3"
            />
          );
        })}

        {/* Nodes */}
        {NODES.map(n => (
          <g
            key={n.id}
            onMouseEnter={() => setActive(n)}
            onMouseLeave={() => setActive(null)}
            style={{ cursor: "pointer" }}
          >
            <circle cx={n.x} cy={n.y} r="8" fill="rgba(212,175,55,0.15)" />
            <circle cx={n.x} cy={n.y} r="3.5" fill="#D4AF37">
              <animate attributeName="r" values="3.5;6;3.5" dur="2.4s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="1;0.5;1" dur="2.4s" repeatCount="indefinite" />
            </circle>
            <text x={n.x + 12} y={n.y + 4} fill="#F8F7F2" fontSize="10" fontFamily="IBM Plex Sans">
              {n.label}
            </text>
          </g>
        ))}
      </svg>

      {active && (
        <div className="absolute top-4 right-4 bg-white/95 border border-[#D4AF37]/40 px-4 py-3 text-sm w-[220px]">
          <div className="eyebrow" style={{ color: "#3399FF" }}>{active.region}</div>
          <div className="font-display text-lg text-[#0B1F3A] leading-tight mt-1">{active.label}</div>
          <div className="text-xs text-[#5F6B7A] mt-1">{active.sector}</div>
        </div>
      )}
    </div>
  );
}
